/**
 * Formatterar aktuellt datum och tid som en sträng på formatet "YYYY-MM-DD HH:MM".
 * @returns {string} - Aktuellt datum och tid.
 */
export const formatCurrentDateTime = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // JavaScript months are 0-based.
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * Formatterar diarienummer till ett standardformat.
 * Om inget diarienummer tillhandahålls returneras en tom sträng.
 *
 * @param {string} diarienummer - Diarienumret som ska formateras.
 * @returns {string} Formaterat diarienummer på formatet 'NNNNNN/NNNN', eller en tom sträng om inget diarienummer anges.
 */
export const formatDiarienummer = (diarienummer: string | undefined): string => {
  if (!diarienummer) {
    diarienummer = '';
  } else if (diarienummer.length >= 5) {
    //Lägg till ett '/' före år-komponenten i diarienumret (före de 4 sista siffrorna)
    diarienummer = diarienummer.substring(0, diarienummer.length - 4) + '/' + diarienummer.slice(-4);
  }
  return diarienummer;
};

/**
 * Formaterar ett datum till en sträng i formatet YYYY-MM-DD HH:MM.
 * Om det angivna datumet är null, undefined, en ogiltig datumsträng, eller inte ett giltigt Date-objekt, returneras en tom sträng.
 *
 * @param {Date | string | null | undefined} date - Datumet som ska formateras. Kan vara en datumsträng, null, undefined, eller inte ett giltigt Date-objekt.
 * @returns {string} En sträng som representerar det formaterade datumet, eller en tom sträng om inget giltigt datum angivits.
 */
export const formatDateYYYYMMDDHHMM = (date: Date | string | null | undefined): string => {
  // Kontrollera om datumet är en sträng och försök konvertera till Date-objekt
  if (typeof date === 'string') {
    date = new Date(date);
  }

  // Kontrollerar om datumet är null, undefined, eller inte ett giltigt Date-objekt efter konvertering
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hour}:${minute}`;
};

export const formatDateYYYYMMDD = (date: Date | string | null | undefined): string => {
    // Kontrollera om datumet är en sträng och försök konvertera till Date-objekt
    if (typeof date === 'string') {
        date = new Date(date);
    }

    // Kontrollerar om datumet är null, undefined, eller inte ett giltigt Date-objekt efter konvertering
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

/**
 * Formatterar en sträng som beskriver filstorleken med lämpliga enhet.
 * @param {number} fileSizeInBytes - Storleken på filen i byte.
 * @returns {string} - En sträng som beskriver filstorleken med lämpliga enhet Bytes, KB, MB eller GB.
 */
export const formatFileSizeWithNormalizedUnit = (fileSizeInBytes: number): string => {
  if (fileSizeInBytes < 1024) {
    return fileSizeInBytes + ' Bytes';
  } else if (fileSizeInBytes < 1024 * 1024) {
    return (fileSizeInBytes / 1024).toFixed(2) + ' KB';
  } else if (fileSizeInBytes < 1024 * 1024 * 1024) {
    return (fileSizeInBytes / (1024 * 1024)).toFixed(2) + ' MB';
  } else {
    return (fileSizeInBytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  }
};
